// Centered pop-up editor for a lab's operations & schedule. Keeps the heavy form
// off the detail page (one long scroll) — opened from the compact summary card.
// Owns the form value, validation and the save call; hands the saved record back
// to the parent via onSaved so the summary card refreshes.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Loader2, AlertCircle, Clock } from "lucide-react";
import { saveLabOperations } from "../../services/diagnosticLabsService";
import OperationsForm, { toOperationsForm, buildOperationsPayload, validateOperations } from "./OperationsForm";

const OperationsModal = ({ open, onClose, role, labId, labName, operations, onSaved }) => {
  const [form, setForm] = useState(() => toOperationsForm(operations));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Re-hydrate from the latest record every time the modal opens.
  useEffect(() => {
    if (open) { setForm(toOperationsForm(operations)); setErrors({}); setError(""); setSubmitted(false); }
  }, [open, operations]);

  // Once the user has tried to save, validate live so errors clear as they fix
  // fields (instead of leaving stale messages from the first attempt).
  const handleFormChange = (next) => {
    setForm(next);
    if (error) setError("");
    if (submitted) setErrors(validateOperations(next));
  };

  const handleSave = async () => {
    setSubmitted(true);
    const e = validateOperations(form);
    if (Object.keys(e).length) {
      setErrors(e);
      setError("Please fix the highlighted fields before saving.");
      return;
    }
    setErrors({});
    setSaving(true);
    setError("");
    try {
      const data = await saveLabOperations(role, labId, buildOperationsPayload(form), !!operations);
      onSaved(data);
      onClose();
    } catch (ex) {
      setError(ex.message || "Failed to save operations.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="absolute inset-0 bg-[#0F4C81]/30 backdrop-blur-[2px]"
            onClick={() => !saving && onClose()}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#EEF3F8] bg-gradient-to-r from-[#F7FAFD] to-transparent flex items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-10 h-10 rounded-xl bg-[#0F4C81]/[0.08] text-[#0F4C81] flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-[#0F4C81]">Operations &amp; Schedule</h3>
                  <p className="text-xs text-gray-400 truncate">
                    {labName}{operations ? "" : " · not configured yet"}
                  </p>
                </div>
              </div>
              <button onClick={() => !saving && onClose()} aria-label="Close" disabled={saving}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body — the only scroll container */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <OperationsForm value={form} onChange={handleFormChange} disabled={saving} errors={errors} />
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0">
              <AnimatePresence initial={false}>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="mb-3 text-xs text-red-600 flex items-center gap-1.5 overflow-hidden">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
                  </motion.p>
                )}
              </AnimatePresence>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => !saving && onClose()} disabled={saving}
                  className="px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2.5 bg-[#0F4C81] text-white rounded-xl text-sm font-medium hover:bg-[#0C3E6B] shadow-sm disabled:opacity-50 flex items-center gap-2 transition-all">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving…" : "Save operations"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default OperationsModal;
